import { useCallback, useRef, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useIsMutating } from "@tanstack/react-query";
import {
    ActionButton,
    Button,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Input,
    Select,
    TextArea,
} from "@jhool-io/fe-components";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import * as FilePicker from "../../../../shared-ui/FilePicker/FilePicker";
import * as Progress from "../../../../shared-ui/Progress/Progress";
import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import {
    IEditNoShowPsychNotePayload,
    IGetClientNotes,
    ISessionNote,
} from "../../../../modules/notes/types/notes.types";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import { NO_SHOW_PSYCH_NOTE_LABELS } from "../../../../utils/constants";
import { IViewFile } from "../../../../utils/types";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import DeleteIcon from "../../../../icons/Delete";

type Option = {
    label: React.ReactNode;
    value: string;
};

interface EditNoShowPsychNoteProps {
    noteDetails: ISessionNote;
}

export default function EditNoShowPsychNote({
    noteDetails,
}: EditNoShowPsychNoteProps) {
    const [showPrompt, setShowPrompt] = useState(false);
    const [selectedNote, setSelectedNote] = useState({} as IGetClientNotes);

    const [docInView, setDocInView] = useState<IViewFile | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);

    const MissedAppointmentTypesForSelect = [
        { label: "Med Management", value: "med_management" },
        { label: "Testing Administration", value: "testing_administration" },
        { label: "Testing Eval/Intake", value: "testing_eval_intake" },
        { label: "Therapy", value: "therapy" },
    ];

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };

    const isSigning = useRef(false);

    const isAddDocMutating = useIsMutating(["add-note-content-document"]);
    const isDeleteDocMutating = useIsMutating(["delete-note-content-document"]);

    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    const {
        handleAutoSaveNote,
        handleEditDraftNote,
        handleSignNote,
        handleAddNoteDocument,
        handleDeleteNoteContentDocument,
        uploadProgress,
    } = useEditNote();

    const {
        register,
        handleSubmit,
        getValues,
        control,
        setValue,
        watch,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditNoShowPsychNotePayload>({
        resolver: yupResolver(
            yup.object({
                missed_appointment_type: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string(),
                }),
                no_show_reason: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string(),
                }),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: {
            missed_appointment_type:
                noteDetails?.note_content?.missed_appointment_type || "",
            no_show_reason: noteDetails?.note_content?.no_show_reason || "",
            signature: noteDetails?.note_content?.signature || "",
        },
        shouldFocusError: false,
    });

    const watchedValues = watch();

    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue(
            "missed_appointment_type",
            note.note_content?.missed_appointment_type || ""
        );
        setValue("no_show_reason", note.note_content.no_show_reason);
        setShowPrompt(false);
    };

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...acceptedFiles, ...selectedFiles]);
            } else {
                setSelectedFiles([...acceptedFiles]);
            }
        },
        [selectedFiles]
    );

    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <>
            {docInView ? (
                <Dialog open={Boolean(docInView)}>
                    <DialogContent
                        title={
                            docInView?.document_name ??
                            "Note content attachment"
                        }
                        handleCloseDialog={() => {
                            setDocInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewFile
                            url={docInView.url}
                            contentType={
                                docInView?.metadata?.content_type ?? ""
                            }
                            title={
                                docInView?.document_name ??
                                "Note content attachment"
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />

            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}

            <form
                onSubmit={handleSubmit(handleSignNote)}
                aria-label="general update form"
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <div className="fg">
                    <Controller
                        name="missed_appointment_type"
                        control={control}
                        render={({ field }) => (
                            <Select
                                label={
                                    NO_SHOW_PSYCH_NOTE_LABELS.missed_appointment_type
                                }
                                isRequired
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    MissedAppointmentTypesForSelect.find(
                                        (opt) => opt.value === field.value
                                    ) || null
                                }
                                options={MissedAppointmentTypesForSelect}
                                hasError={!!errors.missed_appointment_type}
                                errorText={
                                    errors.missed_appointment_type?.message
                                }
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("no_show_reason")}
                        label={NO_SHOW_PSYCH_NOTE_LABELS.no_show_reason}
                        errorText={errors.no_show_reason?.message}
                        hasError={!!errors.no_show_reason}
                        isRequired
                    />
                </div>
                <h3 className="note-label-opt">
                    {NO_SHOW_PSYCH_NOTE_LABELS.files}
                </h3>
                <div className="fg flex flex-col gap-y-[--spacing-form-items]">
                    <FilePicker.FilePicker
                        onDrop={onDrop}
                        disabled={isAddDocMutating > 0}
                    >
                        <FilePicker.FilePickerIcon />
                        <FilePicker.FilePickerMeta description="Click to upload note files" />
                    </FilePicker.FilePicker>
                    {selectedFiles && selectedFiles.length > 0 ? (
                        <div className="flex flex-col gap-y-[--spacing-items]">
                            <FilePicker.FilePickerSelectedFiles
                                selectedFiles={selectedFiles}
                                handleRemoveSelectedFile={
                                    handleRemoveSelectedFile
                                }
                                wrapperClassName="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] grid-flow-row flex-row gap-[--spacing-items]"
                            />
                            <Button
                                className="self-start"
                                onClick={() =>
                                    handleAddNoteDocument(selectedFiles, () =>
                                        setSelectedFiles(null)
                                    )
                                }
                                disabled={isAddDocMutating > 0}
                            >
                                Save files
                            </Button>
                            {uploadProgress > 0 && uploadProgress < 100 && (
                                <Progress.Root value={uploadProgress}>
                                    <Progress.Label>
                                        Uploading {selectedFiles.length}{" "}
                                        {selectedFiles.length === 1
                                            ? "file"
                                            : "files"}
                                        ...
                                    </Progress.Label>
                                    <Progress.Value />
                                    <Progress.Track>
                                        <Progress.Indicator />
                                    </Progress.Track>
                                </Progress.Root>
                            )}
                        </div>
                    ) : null}
                    {noteDetails?.note_content_documents &&
                    noteDetails?.note_content_documents.length > 0 ? (
                        <div className="flex flex-col gap-y-[var(--spacing-items)]">
                            {noteDetails.note_content_documents.map((doc) => (
                                <div
                                    key={doc.note_content_document_id}
                                    className="flex items-center gap-x-3"
                                >
                                    <ActionButton
                                        variant="can-hover"
                                        size="auto"
                                        className="h-7 !text-text text-caption underline"
                                        actionTitle="View file"
                                        onClick={() => {
                                            setDocInView(doc);
                                        }}
                                    >
                                        <span>{`${displayNameInRightFormat(
                                            {
                                                firstName:
                                                    doc.uploaded_by.first_name,
                                                lastName:
                                                    doc.uploaded_by.last_name,
                                            },
                                            false
                                        )} -  ${doc.document_name}`}</span>
                                    </ActionButton>
                                    <ActionButton
                                        variant="can-hover"
                                        aria-label="delete icon"
                                        size="auto"
                                        className="h-7"
                                        onClick={() =>
                                            handleDeleteNoteContentDocument(
                                                doc.note_content_document_id
                                            )
                                        }
                                        disabled={isDeleteDocMutating > 0}
                                        actionTitle="Delete file from note content"
                                    >
                                        <DeleteIcon />
                                    </ActionButton>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>

                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={!!errors.signature}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                    />
                </div>
            </form>
        </>
    );
}
